import { observer } from 'mobx-react-lite';

import {
  CartSiteAddress,
  useCartContext,
} from 'screens/AccessExpressV3/CartProvider';
import { ProductBody } from '../../productFlow';
import Locations from '../../Locations/Locations';
import clsx from 'clsx';
import { useAuthUser } from 'hooks/useAuthUser';
import { postQuote, updateOrder } from 'api/accessexpressv3/api';
import { postQuoteToRequest } from 'screens/AccessExpressV3/AccessProductDetails/utils';
import { useEffect, useRef, useState } from 'react';
import { SearchAccessAddressBar } from '../../Locations/SearchAccessAddressBar';
import { QuoteLocation } from 'api/accessexpress/schema';
import axios from 'axios';
import { ProductCategory } from 'screens/AccessExpressV3/accessExpress.types';
import Add from 'react-ionicons/lib/Add';
import { animate } from 'framer-motion';
import { buildItemPayload } from 'screens/AccessExpressV3/utils';

let cancelTokenSource = axios.CancelToken.source();

const LocationBody = observer(() => {
  const cart = useCartContext();
  const userInfo = useAuthUser();

  const isAccessProductPage = location.pathname.includes('/access/product');

  const handleLocationsChange = async (locations: Array<CartSiteAddress>) => {
    if (cancelTokenSource) {
      cancelTokenSource?.cancel('Operation canceled due to new request.');
    }
    // Create a new controller for the current request
    cancelTokenSource = axios.CancelToken.source();
    // 1. set new or order locations and update the order locations on BE
    if (!cart.isEditingLocations) cart.setLoadingDraftQuote(true);
    cart.setLocations(locations);
    if (cart.draftId && !cart.isEditingLocations) {
      // NOTE: This logic is duplicated across multiple components (handleFindServices, syncCartWithBackend).
      const dataForPostOrUpdate = {
        additional_details: cart.additionalDetails ?? {},
        locations: cart.locations.map((location) => {
          const order_items = cart
            .itemsByLocation(location.id)
            .filter((item) => item.category === 'Access')
            .map(buildItemPayload);
          return {
            address: {
              ...location,
              id: location.id as string,
            },
            order_items,
            id: location.id as string,
          };
        }),
      };
      const draft = await updateOrder(dataForPostOrUpdate, cart.draftId, {
        cancelToken: cancelTokenSource.token,
      });
      // 2. await endpoint to return order update locations
      // 3. store order locations on state;
      cart.updateStateFromDraft(draft);

      if (!isAccessProductPage) {
        return;
      }

      setTimeout(async () => {
        // 4. create quote
        if (cart.selectedAccessProductCategory === 'DIA') {
          cart.setDIAFlowActive(true);
        }
        await postQuote(
          postQuoteToRequest(userInfo, cart.locations, [
            {
              product_type:
                cart.selectedAccessProductCategory as ProductCategory,
              ...cart.additionalDetails?.configs,
            },
          ]),
          { cancelToken: cancelTokenSource.token },
        )
          .then(async (quoteResponse) => {
            const additionalDetails = {
              configs: cart.additionalDetails?.configs,
              ...(cart.selectedAccessProductCategory === 'Starlink'
                ? { starlink_quote_request: quoteResponse.id }
                : cart.selectedAccessProductCategory === 'DIA'
                  ? { dia_quote_request: quoteResponse.id }
                  : { bb_quote_request: quoteResponse.id }),
            };
            await updateOrder(
              {
                additional_details: additionalDetails,
              },
              draft?.id ?? '',
              { cancelToken: cancelTokenSource.token },
            )
              .then((data) => cart.updateStateFromDraft(data))
              .finally(() => {
                cart.setLoadingDraftQuote(false);
              });
          })
          .catch((error) => {
            if (axios?.isAxiosError(error) && error?.name === 'CanceledError')
              return;
            cart.setLoadingDraftQuote(false);
            console.log('Error creating quote:', error);
          });
        // 5. the request will return quote_id
        // 6. update draft (BE + state) with quote_id
      }, 0);
    }
  };
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setCurrentIndex(() => {
      const _currentIndex = cart.locations.findIndex(
        (l) => l.id === cart.locationActiveId,
      );
      return _currentIndex;
    });
  }, [cart.locationActiveId, cart.locations]);

  const locationRefs = useRef<(HTMLDivElement | null)[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    locationRefs.current = locationRefs.current.slice(0, cart.locations.length);
  }, [cart.locations]);

  useEffect(() => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      const target = locationRefs.current[currentIndex];
      if (container && target) {
        const targetScrollTop = target.offsetTop;
        animate(container.scrollTop, targetScrollTop, {
          duration: 0.6,
          ease: 'easeInOut',
          onUpdate: (latest) => (container.scrollTop = latest),
        });
      }
    }
  }, [currentIndex]);

  if (cart.isEditingLocations) {
    return (
      <Locations locations={cart.locations} onChange={handleLocationsChange} />
    );
  }

  const manualShippingAssignmentMethod =
    cart.shippingAssignmentMethod === 'manual';

  const renderEmptyState = (
    <div className="rounded border border-dashed border-stroke-base-default p-2 text-center text-xs font-bold text-button-content-primary-disabled">
      No services added
    </div>
  );

  return (
    <div className="min-h-full overflow-hidden">
      <div
        className="vertical-scrollbar -mr-2 h-full overflow-y-scroll pr-2 hover:-mr-[0px] hover:pr-[0px]"
        ref={scrollRef}
      >
        <div className="relative flex h-full flex-col gap-8">
          {cart.locations.length ? (
            cart.locations.map((location, index) => (
              <div
                key={location.id}
                ref={(el) => (locationRefs.current[index] = el)}
                className={clsx(
                  'relative flex flex-col gap-2',
                  !manualShippingAssignmentMethod && {
                    'pointer-events-none opacity-40':
                      cart.locationActiveId !== location.id,
                    'min-h-full': index === cart.locations.length - 1,
                    'min-h-[calc(100%-122px)]':
                      index !== cart.locations.length - 1,
                  },
                )}
              >
                <SearchAccessAddressBar
                  onAddressSelected={() => {}}
                  className="flex [&_.search-bar-label]:w-[302px] [&_.search-bar-label]:py-0.5 [&_.search-bar-label]:text-sm [&_button]:hidden"
                  value={location as unknown as QuoteLocation}
                  prefixElement={
                    <div className="-ml-2 flex w-4 flex-none justify-end text-sm text-content-base-subdued">
                      {index + 1}.
                    </div>
                  }
                />

                {cart.primaryItemsByLocation(location.id).length > 0 ? (
                  <ProductBody
                    items={cart.primaryItemsByLocation(location.id)}
                  />
                ) : (
                  !manualShippingAssignmentMethod && renderEmptyState
                )}

                {manualShippingAssignmentMethod && (
                  <AddToCartButton
                    locationId={location.id}
                    onAdd={(id) => cart.addUnassignedMobilityItems(id)}
                  />
                )}
              </div>
            ))
          ) : (
            <ProductBody items={cart.unassignedPrimaryMobilityItems} />
          )}
        </div>
      </div>
    </div>
  );
});

export default LocationBody;

const AddToCartButton = ({
  locationId,
  onAdd,
}: {
  locationId?: string | number;
  onAdd: (locationId?: string | number) => void;
}) => {
  return (
    <div
      className={clsx(
        'flex flex-row items-center justify-center gap-1 rounded border border-dashed border-accent-purple bg-accent-purple p-2 text-center text-xs font-bold text-accent-purple',
        'cursor-pointer bg-opacity-[16%] hover:bg-opacity-[32%]',
      )}
      onClick={() => onAdd(locationId || '')}
    >
      Add to cart
      <Add color="currentColor" width="14px" height="14px" />
    </div>
  );
};
